import Image from "next/image";
import React, { useRef, useEffect, useState } from "react";

import { MapActivities } from "./MapActivities";

import { BiMap } from "react-icons/bi";
import { IoChatbubbleOutline } from "react-icons/io5";
import ACTIVE_MUST_SEE_SPHERE_ICON from "@assets/icons/map-icons/active-must-see-sphere.png";
import MUST_SEE_SPHERE_ICON from "@assets/icons/map-icons/must-see-sphere.png";
import ACTIVE_EXPERIENCE_ICON from "@assets/icons/map-icons/active-experience-icon.svg";
import EXPERIENCE_ICON from "@assets/icons/map-icons/exp-icon.svg";
import HOTEL_ICON from "@assets/icons/map-icons/hotel-icon.svg";
import ACTIVE_HOTEL_ICON from "@assets/icons/map-icons/active-hotel-icon.svg";

import type { MapRef } from "react-map-gl";
import ReactMapGl, { Marker } from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import {
  ISpotTopExperiences,
  ISpotTopMustSee,
} from "../../../../types/spotInfo";
import { ISpotToptHotels } from "../../../../types/spotTopHotels";
import {
  SpotTopExperience,
  SpotTopMustSeeExperience,
  SpotToptHotel,
} from "@lib/types";
import { type Section } from "@lib/constants";
import { useTranslation } from "@hooks/useTranslation";
import { useRouter } from "next/router";
import { languageCheck } from "@shared/functions";
import { useCurrencyRate } from "@hooks/useCurrencyRate";
import { MapActivitiesSearch } from "./MapActivities-search";

const MAPBOX_TOKEN = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN!; // Set your mapbox token here

type LatLngLiteral = { latitude?: number; longitude?: number };
type CombinedSpotData = {
  experiences: SpotTopMustSeeExperience[];
  "places-to-visit": SpotTopMustSeeExperience[];
};

interface MapBoxProps {
  section: Section;
  experiences: SpotTopMustSeeExperience[];
  placesToVisit: SpotTopMustSeeExperience[];
}

export const MapBoxSearch = ({
  section,
  experiences,
  placesToVisit
}: MapBoxProps) => {

  const { t } = useTranslation();
  const locale = (useRouter().locale ?? "en") as unknown as Locale;
  const router = useRouter();

  const mapRef = useRef<MapRef>(null);
  const mapRefContainer = useRef<HTMLDivElement>(null);

  const [isSwiperOpen, setIsSwiperOpen] = useState(false);
  const [mapFilters, setMapFilters] = useState<string[]>([]);
  const [activeMarker, setActiveMarker] = useState<LatLngLiteral>({});
  const [activeSlide, setActiveSlide] = useState<number>(-1);
  const [combinedSpotData, setCombinedSpotData] = useState<CombinedSpotData>({
    experiences: [],
    ["places-to-visit"]: [],
  });
  const [viewPort, setViewPort] = useState({
    latitude: placesToVisit?.[0]?.latitude,
    longitude: placesToVisit?.[0]?.longitude,
    zoom: placesToVisit[0]?.city?.country?.locality_zoom != null ?
      Number(
        placesToVisit[0]?.city?.country?.locality_zoom
          .replace(",", ".")
          .replace("z", "")
      ) * 1.2 : 6,
  });


  const mapFilterLabels = [
   {
      name: t("spotprofile.map.to_visit"),
      title: "places-to-visit",
      icon: <IoChatbubbleOutline size={14} color={"#00F3CD"} />,
    },/*
    {
      name: t("spotprofile.experiences.title"),
      title: "experiences",
      icon: <BiMap size={14} color={"#00F3CD"} />,
    },*/
  ];

  const handleMapFilters = (mapFilterTitle: string) => {
    // check in map filters if it had this spot title
    if (mapFilters.includes(mapFilterTitle)) {
      setMapFilters((prevMapFilters) =>
        prevMapFilters.filter(
          (prevMapFilter) => prevMapFilter !== mapFilterTitle
        )
      );
      // clear the proprety that has the same name of the spot title
      setCombinedSpotData({ ...combinedSpotData, [mapFilterTitle]: [] });
    } else {
      setMapFilters((prevMapFilters) => [mapFilterTitle, ...prevMapFilters]);
      mapFilterTitle === "experiences"
        ? setCombinedSpotData({
            ...combinedSpotData,
            [mapFilterTitle]: experiences,
          })
        : 
         setCombinedSpotData({
            ...combinedSpotData,
            [mapFilterTitle]: placesToVisit,
          })
       
    }
    setActiveMarker({});
    setIsSwiperOpen(false);
  };

  const handleSetActiveMarker = (
    latitude: number,
    longitude: number,
    markerIndex: number
  ) => {
    const { pathname, query } = router;
    router.replace(
      {
        pathname,
        query: { ...query, markerId: markerIndex, latitude, longitude },
      },
      undefined,
      {
        shallow: true,
      }
    );
    setActiveMarker({
      latitude: latitude,
      longitude: longitude,
    });
    setActiveSlide(markerIndex);
    mapRef.current?.flyTo({
      center: [longitude, latitude],
      duration: 1500,
    });
    !isSwiperOpen && setIsSwiperOpen(true);
  };

  const handleCloseSwiper = () => {
    setActiveMarker({});
    setActiveSlide(-1);
    setIsSwiperOpen(false);
  };

  // selecting by default filter label based on gallery selection
  useEffect(() => {
    handleMapFilters(section);
    const rect = mapRefContainer.current?.getBoundingClientRect();
    window.scrollTo({
      top: rect?.top! + window.scrollY - 210,
      behavior: "smooth",
    });
  }, []);

  useEffect(() => {
    if (router.query.markerId) {
      const markerId = Number(router.query.markerId);
      const latitude = Number(router.query.latitude);
      const longitude = Number(router.query.longitude);
      setActiveMarker({ latitude, longitude });
      setActiveSlide(markerId);
      setIsSwiperOpen(true);
      mapRef.current?.flyTo({
        center: [longitude, latitude],
        duration: 1500,
      });
    }
  }, [router.query.markerId]);

  // check if url contains expand_activities query to open swiper
  // useEffect(() => {
  //   if (router.query.expand_activities === "true") {
  //     setIsSwiperOpen(true);
  //   }
  // }, [router.query.expand_activities]);

  const currencyRate = useCurrencyRate();



  return (
    <div className="h-[70vh] relative" ref={mapRefContainer}>
      {/* MAP FILTERS */}
      <div className="absolute z-20 flex items-center w-full h-10 top-8">
        <div className="flex items-center h-full pl-3 pr-2 rounded-r-3xl min-w-1/2 bg-white/40">
          {mapFilterLabels?.map((mapFilterLabel, idx) => {
            return (
              <div
                key={mapFilterLabel.title + idx}
                className={
                  "flex px-2 py-1 mr-2 text-white rounded-3xl border-[1px]" +
                  `${
                    mapFilters?.includes(mapFilterLabel.title)
                      ? " border-primary-color bg-black/60 outline-none"
                      : " border-slate-700 outline-none"
                  }`
                }
                role={"button"}
                onClick={() => handleMapFilters(mapFilterLabel.title)}
              >
                <span className="align-middle">{mapFilterLabel.icon}</span>
                <span className="ml-1 text-[10px] font-extralight uppercase">
                  {mapFilterLabel.name}
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="w-full h-full mt-3">
        <ReactMapGl
          // mapLib={mapboxgl}
          // onMove={(evt) => setViewState(evt.viewState)}
          reuseMaps
          ref={mapRef}
          mapboxAccessToken={MAPBOX_TOKEN}
          {...viewPort}
          style={{ width: "100%", height: "100%" }}
          mapStyle="mapbox://styles/mapbox/dark-v11"
          styleDiffing
          maxZoom={15}
          onMove={(evt) => setViewPort(evt.viewState)}
        >
          {mapFilters.includes("experiences") &&
            experiences.map((el, activeSlideIndex) => {
                return (
                  <Marker
                    key={el.english_name + activeSlideIndex}
                    longitude={el?.longitude}
                    latitude={el?.latitude} 
                    color={"#00F3CD"}
                    style={
                      activeMarker?.latitude === el?.latitude &&
                      activeMarker?.longitude === el?.longitude
                        ? { zIndex: 100 }
                        : { zIndex: 0 }
                    }
                    onClick={() =>
                      handleSetActiveMarker(
                        el?.latitude,
                        el?.longitude,
                        activeSlideIndex
                      )
                    }
                  >
                    <div
                      className={`grid place-items-center ${
                        activeMarker?.latitude === el?.latitude &&
                        activeMarker?.longitude === el?.longitude
                          ? "gap-[.8rem]"
                          : "gap-[.4rem]"
                      }`}
                    >
                      <div
                        className={`w-7 h-7 ${
                          activeMarker?.latitude === el?.latitude &&
                          activeMarker?.longitude === el?.longitude
                            ? "scale-125"
                            : "scale-90"
                        } transition duration-150 ease-in`}
                      >
                        <Image
                          src={
                            activeMarker?.latitude === el?.latitude &&
                            activeMarker?.longitude === el?.longitude
                              ? ACTIVE_EXPERIENCE_ICON
                              : EXPERIENCE_ICON
                          }
                          alt={"must-see-picture"}
                          className={"object-cover w-[100%]"}
                          width={100}
                          height={100}
                        />
                      </div>
                      <div className="py-[.05rem] px-2 text-[.7rem] font-semibold text-black bg-primary-color rounded-full">
                      {Math.round(currencyRate.rate * el.activity.reduce(
                            (price, curr) =>
                              isNaN(+price)
                                ? "0"
                                : Number(
                                    languageCheck(
                                      curr.minimum_price_en,
                                      curr.minimum_price_es,
                                      curr.minimum_price_fr,
                                      curr.minimum_price_ru
                                    )
                                  ) > Number(price)
                                ? price
                                : curr.minimum_price_en,
                            languageCheck(
                              el.activity?.[0]?.minimum_price_en,
                              el.activity?.[0]?.minimum_price_es,
                              el.activity?.[0]?.minimum_price_fr,
                              el.activity?.[0]?.minimum_price_ru
                            )
                          ))}{currencyRate.symbol}
                      </div>
                    </div>
                  </Marker>
                );
            })}

          {mapFilters.includes("places-to-visit") &&
            placesToVisit.map((el, activeSlideIndex) => {
              console.log('el?.latitude',el?.longitude,
              combinedSpotData?.experiences.length + activeSlideIndex)
                return (
                  <Marker
                    key={el.english_name + activeSlideIndex}
                    longitude={el?.longitude}
                    latitude={el?.latitude}
                    color={"#FFCE40"}
                    style={
                      activeMarker?.latitude === el?.latitude &&
                      activeMarker?.longitude === el?.longitude
                        ? { zIndex: 100 }
                        : { zIndex: 0 }
                    }
                    onClick={() =>
                      handleSetActiveMarker(
                        el?.latitude,
                        el?.longitude,
                        combinedSpotData?.experiences.length + activeSlideIndex
                      )
                    }
                  >
                    <div
                      className={`grid place-items-center ${
                        activeMarker?.latitude === el?.latitude &&
                        activeMarker?.longitude === el?.longitude
                          ? "gap-[.8rem]"
                          : "gap-[.4rem]"
                      }`}
                    >
                      <div
                        className={`w-7 h-7 ${
                          activeMarker?.latitude === el?.latitude &&
                          activeMarker?.longitude === el?.longitude
                            ? "scale-125"
                            : "scale-100"
                        } transition duration-150 ease-in`}
                        // style={{ transform: "translate(-50%,-50%)" }}
                      >
                        <div className="flex items-center justify-center w-8 h-8">
                          <Image
                            src={el?.webposts[0]?.post?.thumbnail_url}
                            alt={"must-see-picture"}
                            className={
                              "object-cover h-[88%] w-[88%] rounded-full"
                            }
                            width={100}
                            height={100}
                          />
                        </div>
                        <div className="absolute z-20 w-8 -top-0">
                          <Image
                            src={
                              activeMarker?.latitude === el?.latitude &&
                              activeMarker?.longitude === el?.longitude
                                ? ACTIVE_MUST_SEE_SPHERE_ICON
                                : MUST_SEE_SPHERE_ICON
                            }
                            alt={"must-see-sphere"}
                            className={"object-cover max-h-full w-full"}
                            width={100}
                            height={100}
                          />
                        </div>
                      </div>
                      <div className="ml-[.4rem] py-[.05rem] px-2 text-[.7rem] font-semibold text-black bg-primary-color rounded-full">
                        {Math.round(currencyRate.rate * el.activity.reduce(
                            (price, curr) =>
                              isNaN(+price)
                                ? "0"
                                : Number(
                                    languageCheck(
                                      curr.minimum_price_en,
                                      curr.minimum_price_es,
                                      curr.minimum_price_fr,
                                      curr.minimum_price_ru
                                    )
                                  ) > Number(price)
                                ? price
                                : curr.minimum_price_en,
                            languageCheck(
                              el.activity?.[0]?.minimum_price_en,
                              el.activity?.[0]?.minimum_price_es,
                              el.activity?.[0]?.minimum_price_fr,
                              el.activity?.[0]?.minimum_price_ru
                            )
                          ))}{currencyRate.symbol}
                      </div>
                    </div>
                  </Marker>
                );
            })}

       
        </ReactMapGl>

        {isSwiperOpen && (
          <MapActivitiesSearch
            data={
              [
                ...combinedSpotData?.experiences!,
                ...combinedSpotData?.["places-to-visit"]!
              ] as unknown as (ISpotTopMustSee & ISpotTopMustSee)[]
            }
            activeSlide={activeSlide}
            handleCloseSwiper={handleCloseSwiper}
            handleSetActiveMarker={handleSetActiveMarker}
            activeMarkerCoordinates={{langitude: activeMarker?.longitude, latitude: activeMarker?.latitude}}
          />
        )}
      </div>
    </div>
  );
};
